import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  afterNextRender,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  viewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SpinnerComponent } from '../../../core/components/spinner/spinner.component';
import { ParentViewportService } from '../../../core/services/parent-viewport.service';

/**
 * Endereços que a tela mesma produz: `blob:` do arquivo escolhido no navegador e
 * caminhos relativos dos assets. Qualquer outro fica fora do iframe — marcar uma
 * URL como confiável sem olhar abriria a porta para conteúdo de terceiros.
 */
function isUrlDaAplicacao(url: string): boolean {
  return url.startsWith('blob:') || /^(\.\/)?assets\//.test(url);
}

/**
 * Modal que exibe um PDF com o visualizador do próprio navegador.
 *
 * Segue o `app-alert-dialog`: fundo sobre a aplicação inteira e caixa na região
 * que o usuário enxerga no portal. As diferenças são o tamanho — o documento
 * precisa da altura toda para ser lido — e a saída: só o botão X fecha. Clique no
 * fundo e Esc não fecham, para a leitura não ser interrompida por engano.
 */
@Component({
  selector: 'app-pdf-viewer-dialog',
  standalone: true,
  imports: [SpinnerComponent],
  templateUrl: './pdf-viewer-dialog.component.html',
  styleUrl: './pdf-viewer-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfViewerDialogComponent implements OnDestroy {
  private readonly sanitizer = inject(DomSanitizer);

  /** Nome do arquivo, exibido no topo e usado como título do documento. */
  readonly nome = input.required<string>();

  /** Endereço do PDF; ver `isUrlDaAplicacao` para os aceitos. */
  readonly url = input.required<string>();

  readonly fechar = output<void>();

  protected readonly regiaoVisivel = inject(ParentViewportService).regiaoVisivel;

  /**
   * Enquanto o navegador não termina de carregar o documento. Volta a `true` se o
   * endereço mudar, porque aí é outro documento chegando.
   *
   * O indicador é local, e não o `LoaderService`: o overlay global fica acima do
   * modal e cobriria o X, e há navegadores (vários móveis) que baixam o PDF em vez
   * de exibi-lo e nunca avisam o fim do carregamento — a tela ficaria travada.
   */
  protected readonly carregando = linkedSignal(() => {
    this.url();
    return true;
  });

  protected readonly urlSegura = computed<SafeResourceUrl | null>(() => {
    const url = this.url();
    return isUrlDaAplicacao(url) ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  private readonly botaoFechar = viewChild.required<ElementRef<HTMLButtonElement>>('botaoFechar');

  /** Quem abriu o modal recebe o foco de volta ao fechar. */
  private readonly focoAnterior = document.activeElement as HTMLElement | null;

  constructor() {
    afterNextRender(() => this.botaoFechar().nativeElement.focus());
  }

  ngOnDestroy(): void {
    this.focoAnterior?.focus?.();
  }

  /**
   * O iframe avisa `load` já ao entrar na página, pelo `about:blank` inicial, antes
   * de o endereço do PDF ser aplicado. Esse primeiro aviso não conta. Quando o
   * documento é de outra origem a leitura do endereço é barrada — e aí só pode ser
   * o PDF, já que o `about:blank` é sempre da mesma origem.
   */
  onDocumentoCarregado(event: Event): void {
    const frame = event.target as HTMLIFrameElement;

    try {
      if (frame.contentWindow?.location.href === 'about:blank') {
        return;
      }
    } catch {
      // Origem diferente: é o documento.
    }

    this.carregando.set(false);
  }

  /** Única saída do modal: o botão X. */
  onFechar(): void {
    this.fechar.emit();
  }
}
